/**
 * @version 1.0.0
 * @published 2026-04-08
 * @history
 *   1.0.0 — Initial publish from prototype/pages/Perks/EmberBlocksAnimation.tsx.
 *           Animated 49-block ember pile that drops chaotically into a
 *           pyramid pile, pauses, converges into a 7x7 grid square, fades,
 *           and loops. Used as the hero graphic on the Perks landing page.
 */

import { motion, useAnimation } from "motion/react";
import React, { useCallback, useEffect, useRef, useState } from "react";

const EMBER = "rgb(var(--color-accent))";
const BLOCK_SIZE = 8;

// Pyramid shape — wide at bottom, narrow at top (RIGHT-SIDE UP)
const PYRAMID_ROWS = [
    { count: 10, row: 0 },
    { count: 9, row: 1 },
    { count: 8, row: 2 },
    { count: 7, row: 3 },
    { count: 6, row: 4 },
    { count: 5, row: 5 },
    { count: 4, row: 6 }
];

const PYRAMID_MAX_COUNT = PYRAMID_ROWS[0].count;
const PYRAMID_WIDTH = PYRAMID_MAX_COUNT * BLOCK_SIZE;
const PYRAMID_HEIGHT = PYRAMID_ROWS.length * BLOCK_SIZE;

const GRID_SIZE = 7;
const ASSEMBLED_SIZE = GRID_SIZE * BLOCK_SIZE;

const CONTAINER_WIDTH = PYRAMID_WIDTH;
const CONTAINER_HEIGHT = PYRAMID_HEIGHT + 4;

const DROP_ORIGIN_X = CONTAINER_WIDTH / 2 - BLOCK_SIZE / 2;
const DROP_ORIGIN_Y = -100;

function seededRandom(seed: number): number {
    const x =
        Math.sin(seed * 127.1) * 43758.5453 +
        Math.sin(seed * 311.7) * 28461.3217;
    return x - Math.floor(x);
}

interface BlockPosition {
    x: number;
    y: number;
}

interface BlockData {
    id: number;
    pyramidPos: BlockPosition;
    gridPos: BlockPosition;
    dropDelay: number;
    reassembleDelay: number;
    dropStartXOffset: number;
    fallRotation: number;
    bounceXOffset: number;
    bounceHeight: number;
    secondBounceHeight: number;
    slideX: number;
    fallDurationVariance: number;
    dropOrder: number;
    settleRotation: number;
    pileJitterX: number;
    pileJitterY: number;
    rowDrift: number;
    overshootX: number;
    thirdBounceHeight: number;
}

function generateBlocks(): BlockData[] {
    const blocks: BlockData[] = [];
    let id = 0;

    const rowDrifts: number[] = PYRAMID_ROWS.map((_, i) => {
        const drift = (seededRandom(i * 97 + 53) - 0.5) * 4;
        return drift;
    });

    for (let rowIdx = 0; rowIdx < PYRAMID_ROWS.length; rowIdx++) {
        const rowDef = PYRAMID_ROWS[rowIdx];
        const rowWidth = rowDef.count * BLOCK_SIZE;
        const baseStartX = (CONTAINER_WIDTH - rowWidth) / 2;
        const baseY = CONTAINER_HEIGHT - BLOCK_SIZE - rowDef.row * BLOCK_SIZE;

        const drift = rowDrifts[rowIdx];

        for (let col = 0; col < rowDef.count; col++) {
            const r = (seed: number): number => seededRandom(id * 17 + seed);

            const rowHeightFactor = Math.min(1.8, 1 + rowIdx * 0.18);

            const jitterX = (r(41) - 0.5) * 5 * rowHeightFactor;
            const jitterY = (r(42) - 0.5) * 4 * rowHeightFactor;

            const isEdge = col === 0 || col === rowDef.count - 1;
            const edgeOffset = isEdge
                ? (r(43) - 0.4) * 5 * (col === 0 ? -1 : 1)
                : 0;

            const isFlat = r(47) < 0.15;
            const baseAngle = isFlat ? r(44) * 2 : 5 + r(44) * 7;
            const rotDir = r(45) > 0.5 ? 1 : -1;
            const adjacentVariance = (r(46) - 0.5) * 3;
            const settleRot = rotDir * (baseAngle + adjacentVariance);

            blocks.push({
                id,
                pyramidPos: {
                    x:
                        baseStartX +
                        col * BLOCK_SIZE +
                        drift +
                        jitterX +
                        edgeOffset,
                    y: baseY + jitterY
                },
                gridPos: { x: 0, y: 0 },
                dropDelay: 0,
                reassembleDelay: 0,
                dropStartXOffset: 0,
                fallRotation: 0,
                bounceXOffset: 0,
                bounceHeight: 0,
                secondBounceHeight: 0,
                slideX: 0,
                fallDurationVariance: 0,
                dropOrder: 0,
                settleRotation: settleRot,
                pileJitterX: jitterX + edgeOffset,
                pileJitterY: jitterY,
                rowDrift: drift,
                overshootX: 0,
                thirdBounceHeight: 0
            });
            id++;
        }
    }

    const gridOffsetX = (CONTAINER_WIDTH - ASSEMBLED_SIZE) / 2;
    const gridOffsetY = (CONTAINER_HEIGHT - ASSEMBLED_SIZE) / 2;

    for (let i = 0; i < blocks.length; i++) {
        const gridRow = Math.floor(i / GRID_SIZE);
        const gridCol = i % GRID_SIZE;
        blocks[i].gridPos = {
            x: gridOffsetX + gridCol * BLOCK_SIZE,
            y: gridOffsetY + gridRow * BLOCK_SIZE
        };
    }

    const gridCenterX = gridOffsetX + ASSEMBLED_SIZE / 2;
    const gridCenterY = gridOffsetY + ASSEMBLED_SIZE / 2;
    let maxDist = 0;
    for (const block of blocks) {
        const dx = block.pyramidPos.x + BLOCK_SIZE / 2 - gridCenterX;
        const dy = block.pyramidPos.y + BLOCK_SIZE / 2 - gridCenterY;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist > maxDist) maxDist = dist;
    }
    for (const block of blocks) {
        const dx = block.pyramidPos.x + BLOCK_SIZE / 2 - gridCenterX;
        const dy = block.pyramidPos.y + BLOCK_SIZE / 2 - gridCenterY;
        const dist = Math.sqrt(dx * dx + dy * dy);
        block.reassembleDelay =
            maxDist > 0 ? ((maxDist - dist) / maxDist) * 0.12 : 0;
    }

    const pyramidBottomCenterX = CONTAINER_WIDTH / 2;
    const pyramidBottomY = CONTAINER_HEIGHT - BLOCK_SIZE;

    const scored = blocks.map((b) => {
        const bCenterX = b.pyramidPos.x + BLOCK_SIZE / 2;
        const bCenterY = b.pyramidPos.y + BLOCK_SIZE / 2;
        const dx = bCenterX - pyramidBottomCenterX;
        const dy = bCenterY - pyramidBottomY;
        const score = Math.abs(dx) * 0.5 + Math.abs(dy) * 1.6;
        return { block: b, score };
    });

    scored.sort((a, b) => a.score - b.score);

    const BASE_STAGGER = 0.045;
    const BURST_COUNT = 5;
    const BURST_STAGGER = 0.025;

    let cumulativeDelay = 0;
    for (let i = 0; i < scored.length; i++) {
        const stagger = i < BURST_COUNT ? BURST_STAGGER : BASE_STAGGER;
        const jitter = (seededRandom(scored[i].block.id * 7 + 3) - 0.5) * 0.055;
        const extraPause =
            seededRandom(scored[i].block.id * 11 + 7) > 0.85 ? 0.04 : 0;
        scored[i].block.dropDelay = cumulativeDelay + jitter;
        scored[i].block.dropOrder = i;
        cumulativeDelay += stagger + extraPause;
    }

    for (const block of blocks) {
        const r = (seed: number): number => seededRandom(block.id * 13 + seed);

        block.dropStartXOffset = (r(1) - 0.5) * 22;

        const finalOffsetFromCenter =
            block.pyramidPos.x + BLOCK_SIZE / 2 - CONTAINER_WIDTH / 2;

        block.fallRotation =
            (r(2) - 0.5) * 180 + (finalOffsetFromCenter > 0 ? 20 : -20);

        const edgeness =
            Math.abs(finalOffsetFromCenter) / (CONTAINER_WIDTH / 2);

        const rowIndex = PYRAMID_ROWS.findIndex(
            (row) =>
                Math.abs(
                    block.pyramidPos.y -
                        block.pileJitterY -
                        (CONTAINER_HEIGHT - BLOCK_SIZE - row.row * BLOCK_SIZE)
                ) < 1
        );
        const isUpperHalf = rowIndex !== -1 && PYRAMID_ROWS[rowIndex].row >= 3;

        const bounceDir = finalOffsetFromCenter >= 0 ? 1 : -1;
        const earlyBlockFactor = Math.max(0, 1 - block.dropOrder / 8);
        const randomDir = r(8) > 0.5 ? 1 : -1;
        const earlyBounce = randomDir * (1.5 + r(3) * 3) * earlyBlockFactor;
        const lateBounce =
            bounceDir *
            (3 + edgeness * 9) *
            (0.7 + r(3) * 0.6) *
            (1 - earlyBlockFactor);
        block.bounceXOffset = earlyBounce + lateBounce;

        const pileDepthFactor = Math.max(
            0,
            1 - block.dropOrder / blocks.length
        );
        const lateBlockFactor = Math.min(1, block.dropOrder / blocks.length);
        block.bounceHeight = isUpperHalf
            ? 3 + r(4) * 5 + lateBlockFactor * 4
            : 5 + r(4) * 6 + pileDepthFactor * 3;

        block.secondBounceHeight = block.bounceHeight * (0.25 + r(5) * 0.25);

        block.thirdBounceHeight = isUpperHalf
            ? block.secondBounceHeight * (0.3 + r(20) * 0.3)
            : 0;

        block.slideX = (r(6) - 0.5) * 2.5;

        block.fallDurationVariance = (r(7) - 0.5) * 0.1;

        block.overshootX = r(9) > 0.6 ? bounceDir * (2 + r(10) * 4) : 0;
    }

    return blocks;
}

const BLOCKS = generateBlocks();

const DROP_FALL_BASE_DURATION = 0.38;
const MAX_DROP_DELAY = Math.max(...BLOCKS.map((b) => b.dropDelay));
const TOTAL_DROP_TIME = MAX_DROP_DELAY + DROP_FALL_BASE_DURATION + 0.8;
const PAUSE_AFTER_PYRAMID = 1.5;
const ANTICIPATION_DURATION = 0.12;
const REASSEMBLE_DURATION = 0.6;
const PAUSE_AFTER_SQUARE = 1.5;
const FADE_OUT_DURATION = 0.35;

type Phase =
    | "dropping"
    | "pyramid-hold"
    | "reassembling"
    | "square-hold"
    | "fading";

export function EmberBlocksAnimation(): React.ReactElement {
    const [phase, setPhase] = useState<Phase>("dropping");
    const [cycleKey, setCycleKey] = useState(0);
    const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

    const schedulePhases = useCallback(() => {
        timersRef.current.forEach(clearTimeout);
        timersRef.current = [];

        setPhase("dropping");

        const t1 = TOTAL_DROP_TIME * 1000;
        const t2 = t1 + PAUSE_AFTER_PYRAMID * 1000;
        const t3 =
            t2 + (ANTICIPATION_DURATION + REASSEMBLE_DURATION + 0.12) * 1000;
        const t4 = t3 + PAUSE_AFTER_SQUARE * 1000;
        const t5 = t4 + FADE_OUT_DURATION * 1000;

        timersRef.current = [
            setTimeout(() => setPhase("pyramid-hold"), t1),
            setTimeout(() => setPhase("reassembling"), t2),
            setTimeout(() => setPhase("square-hold"), t3),
            setTimeout(() => setPhase("fading"), t4),
            setTimeout(() => setCycleKey((k) => k + 1), t5)
        ];
    }, []);

    useEffect(() => {
        schedulePhases();
        return (): void => {
            timersRef.current.forEach(clearTimeout);
        };
    }, [cycleKey, schedulePhases]);

    return (
        <div
            className="relative"
            style={{
                width: CONTAINER_WIDTH,
                height: CONTAINER_HEIGHT,
                overflow: "visible"
            }}
        >
            {BLOCKS.map((block) => (
                <AnimatedBlock
                    key={`${block.id}-${cycleKey}`}
                    block={block}
                    phase={phase}
                />
            ))}
        </div>
    );
}

interface AnimatedBlockProps {
    block: BlockData;
    phase: Phase;
}

function AnimatedBlock({
    block,
    phase
}: AnimatedBlockProps): React.ReactElement {
    const controls = useAnimation();

    const startX = DROP_ORIGIN_X + block.dropStartXOffset;
    const startY = DROP_ORIGIN_Y;

    useEffect(() => {
        if (phase === "dropping") {
            const run = async (): Promise<void> => {
                controls.set({
                    x: startX,
                    y: startY,
                    opacity: 0,
                    scaleX: 1,
                    scaleY: 1,
                    rotate: 0
                });

                await new Promise<void>((resolve) => {
                    setTimeout(resolve, Math.max(0, block.dropDelay) * 1000);
                });

                controls.set({ opacity: 1 });

                const impactY = block.pyramidPos.y + 3;
                const fallDuration =
                    DROP_FALL_BASE_DURATION + block.fallDurationVariance;

                const driftFactor =
                    0.2 + seededRandom(block.id * 31 + 1) * 0.25;
                const midX =
                    startX + (block.pyramidPos.x - startX) * driftFactor;

                await controls.start({
                    x: midX,
                    y: impactY,
                    rotate: block.fallRotation * 0.6,
                    transition: {
                        duration: Math.max(fallDuration, 0.25),
                        ease: [0.45, 0, 0.95, 0.15]
                    }
                });

                const squashIntensity =
                    0.85 + seededRandom(block.id * 23 + 5) * 0.3;
                await controls.start({
                    x: midX,
                    y: block.pyramidPos.y + 1.5,
                    scaleX: 1 + 0.15 * squashIntensity,
                    scaleY: 1 - 0.22 * squashIntensity,
                    rotate: block.fallRotation * 0.7,
                    transition: {
                        duration: 0.03 + seededRandom(block.id * 29 + 2) * 0.03,
                        ease: "easeOut"
                    }
                });

                const bounceX = midX + block.bounceXOffset;
                const bounceDuration =
                    0.09 + seededRandom(block.id * 37 + 3) * 0.06;

                await controls.start({
                    x: bounceX,
                    y: block.pyramidPos.y - block.bounceHeight,
                    scaleX: 0.92,
                    scaleY: 1.08,
                    rotate: block.fallRotation * 0.9,
                    transition: {
                        duration: bounceDuration,
                        ease: "easeOut"
                    }
                });

                const overshootTarget =
                    block.pyramidPos.x + block.overshootX + block.slideX * 0.5;

                await controls.start({
                    x: overshootTarget,
                    y: block.pyramidPos.y + 1,
                    scaleX: 1.06,
                    scaleY: 0.92,
                    rotate: block.fallRotation * 0.4,
                    transition: {
                        duration: 0.09,
                        ease: [0.4, 0, 1, 0.3]
                    }
                });

                if (Math.abs(block.overshootX) > 0.5) {
                    await controls.start({
                        x: block.pyramidPos.x + block.slideX * 0.7,
                        y: block.pyramidPos.y + 0.5,
                        scaleX: 1.02,
                        scaleY: 0.98,
                        rotate: block.settleRotation * 0.6,
                        transition: {
                            duration: 0.08,
                            ease: "easeInOut"
                        }
                    });
                }

                await controls.start({
                    x: block.pyramidPos.x + block.slideX,
                    y: block.pyramidPos.y - block.secondBounceHeight,
                    scaleX: 0.97,
                    scaleY: 1.03,
                    rotate: block.settleRotation * 0.7,
                    transition: {
                        duration: 0.07,
                        ease: "easeOut"
                    }
                });

                if (block.thirdBounceHeight > 0.5) {
                    await controls.start({
                        x: block.pyramidPos.x + block.slideX * 0.3,
                        y: block.pyramidPos.y + 0.5,
                        scaleX: 1.01,
                        scaleY: 0.99,
                        rotate: block.settleRotation * 0.85,
                        transition: {
                            duration: 0.05,
                            ease: [0.4, 0, 1, 0.3]
                        }
                    });
                    await controls.start({
                        x: block.pyramidPos.x,
                        y: block.pyramidPos.y - block.thirdBounceHeight,
                        scaleX: 0.99,
                        scaleY: 1.01,
                        rotate: block.settleRotation * 0.9,
                        transition: {
                            duration: 0.05,
                            ease: "easeOut"
                        }
                    });
                }

                await controls.start({
                    x: block.pyramidPos.x,
                    y: block.pyramidPos.y,
                    scaleX: 1,
                    scaleY: 1,
                    rotate: block.settleRotation,
                    transition: {
                        duration: 0.1,
                        ease: [0.25, 0.46, 0.45, 0.94]
                    }
                });
            };
            void run().catch(console.error);
        } else if (phase === "reassembling") {
            const runReassemble = async (): Promise<void> => {
                await controls.start({
                    y: block.pyramidPos.y - 2,
                    rotate: block.settleRotation * 1.1,
                    transition: {
                        duration: ANTICIPATION_DURATION,
                        ease: "easeOut"
                    }
                });

                if (block.reassembleDelay > 0) {
                    await new Promise<void>((resolve) => {
                        setTimeout(resolve, block.reassembleDelay * 1000);
                    });
                }

                await controls.start({
                    x: block.gridPos.x,
                    y: block.gridPos.y,
                    scaleX: 1,
                    scaleY: 1,
                    rotate: 0,
                    transition: {
                        duration: REASSEMBLE_DURATION - block.reassembleDelay,
                        ease: [0.16, 1, 0.3, 1]
                    }
                });
            };
            void runReassemble().catch(console.error);
        } else if (phase === "fading") {
            void controls.start({
                opacity: 0,
                scaleX: 0.85,
                scaleY: 0.85,
                transition: {
                    duration: FADE_OUT_DURATION,
                    ease: "easeIn"
                }
            });
        }
    }, [phase, controls, block, startX, startY]);

    return (
        <motion.div
            animate={controls}
            initial={{
                x: startX,
                y: startY,
                opacity: 0,
                scaleX: 1,
                scaleY: 1,
                rotate: 0
            }}
            style={{
                position: "absolute",
                width: BLOCK_SIZE,
                height: BLOCK_SIZE,
                backgroundColor: EMBER,
                borderRadius: 0
            }}
        />
    );
}
